import { useEffect, useState } from 'react';

import { BackgroundTask } from 'controllers/types';
import consumer from '../../channels/consumer';
import { devConsole } from '../utils/logUtils';

// background_tasks with these statuses are monitored through a websocket
const pendingStatusStates = ['enqueued', 'started', 'waiting'];

type ChannelData = { status: BackgroundTask['status']; message: string };

/**
 * @deprecated
 * This hook is deprecated and should not be used in new code. Try using one of our new hooks instead:
 * - `useBackgroundTaskChannel`: works more like this hook to watch a single task
 * - `useLessonBTWatcher`: watches all tasks for a lesson
 * - `useLessonSegmentBTWatcher`: watches all tasks for a lesson segment
 */
export function useProgressBarChannel(task?: BackgroundTask, taskDoneFunction?: (data: BackgroundTask) => void) {
  const [taskData, setTaskData] = useState(task || ({} as BackgroundTask)); // empty object to allow consumer to use dot notation

  // disabling because we don't wanna run this effect on taskData changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setInitialTaskState, [task, task?.id, task?.status]);
  function setInitialTaskState() {
    if (task?.id !== taskData.id || task?.status !== taskData.status) {
      setTaskData(task !== undefined ? task : ({} as BackgroundTask));
    }
  }

  useEffect(() => {
    // background_tasks with these statuses are monitored through a websocket
    if (task !== undefined && pendingStatusStates.some(s => task?.status === s)) {
      const channel = consumer.subscriptions.create(
        {
          channel: 'ProgressBarChannel',
          task_id: task.id,
        },
        {
          received(data: ChannelData) {
            console.debug('useProgressBarChannel received', { taskName: task.name, ...data });
            const updatedTask = { ...taskData, ...data };
            setTaskData(updatedTask);
            if (taskDoneFunction) taskDoneFunction(updatedTask);
          },
        },
      );
      return () => {
        channel.unsubscribe();
        consumer.disconnect();
      };
    }
  }, [task, taskData, taskDoneFunction]);

  return taskData;
}

export const generateSafeBGTaskErrorText = (
  // TODO(ege): This argument is not really needed, I am adding it to not grow the scope of my work here.
  // Ideally, value of this argument should be derived from background task name. However, that requires going through
  // all the background task names to make them UI friendly and update all the watchers to watch new names.
  process: 'video generation' | 'publishing' | 'course creation' | 'translation',
  bgTask: BackgroundTask,
) => {
  const safeMessage = `Error during ${process}. -- Job: ${bgTask.id} (${bgTask.job_id}), Details: [${bgTask.task_entity_type}, ${bgTask.task_entity_id || bgTask.task_entity?.id}]`;
  // eslint-disable-next-line camelcase
  const UNSAFE_privateMessage = bgTask.message; // bgTask.message is a private message that is only for our developers' eyes because it exposes our internals
  devConsole.error(safeMessage, UNSAFE_privateMessage);

  return safeMessage;
};
